import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import styled from '@emotion/styled';
import { ButtonType } from 'constants/button';
import { ModalParams, ModalType } from 'constants/modal';
import { useCallback, useEffect, useState } from 'react';
import { Button } from 'ui-components/common/Button';
import { ModalLayout } from 'ui-components/layout/ModalLayout';
import { SafeAreaBottomStyles } from 'ui-components/layout/styles';
import { useShowModal } from 'ui-hooks/useShowModal';
import { ModalHandler } from 'utils/ModalHandler';
import { TextArea } from 'ui-components/common/TextArea';
import { css } from '@emotion/react';
import { isPC } from 'constants/layout';
import { useMedia } from 'ui-hooks/useMediaQuery';
import { RadioButton } from 'ui-components/common/RadioButton';
import { ExternalLinkButton } from 'ui-components/common/ExternalLinkButton';
import { useAuthState } from 'contexts/AuthProvider';
import { showToast } from 'utils/modal';
import { range } from '@diritto/common/utils/array';

export const NpsCampaignModal = () => {
  const { isPc } = useMedia();
  const [checkedIndex, setCheckedIndex] = useState<number>();
  const [comment, setComment] = useState<string>();
  const { profile } = useAuthState();

  const { isShow, revealModal, showModal, hideModal } = useShowModal();
  const [state, setState] = useState<ModalParams[ModalType.NpsCampaign]>();
  const [showRequestInterview, setShowRequestInterview] = useState(false);
  const [attendInterview, setAttendInterview] = useState<string>();

  // 점수 선택 및, 인터뷰요청이 있다면 인터뷰 참석을 하는지 안하는지 선택해야 한다.
  const enableSendButton = checkedIndex !== undefined && (!state?.npsCampaign.requestInterview || attendInterview);

  const handleClose = useCallback(() => {
    state?.onCancel?.();
    hideModal();
  }, [hideModal, state]);

  const handleSubmit = useCallback(() => {
    state?.onSubmit?.({ comment, score: checkedIndex, attendInterview: attendInterview === 'true' });
    if (state?.npsCampaign.requestInterview && attendInterview === 'true') {
      setShowRequestInterview(true);
    } else {
      showToast('제출되었어요.');
      hideModal();
    }
  }, [state, comment, checkedIndex, attendInterview, hideModal]);

  useEffect(() => {
    const listener = (show: boolean, params?: ModalParams[ModalType.NpsCampaign]) => {
      setState(params);

      if (show) {
        setShowRequestInterview(false);
        showModal();
      } else {
        hideModal();
      }
    };

    ModalHandler.addEventListener(ModalType.NpsCampaign, listener);

    return () => {
      ModalHandler.removeEventListener(ModalType.NpsCampaign);
    };
  }, [hideModal, showModal]);
  return (
    <ModalLayout isShow={isShow} revealModal={revealModal} isBottom={!isPc}>
      {!showRequestInterview ? (
        <StyledModal>
          <StyledQuestion>{state?.npsCampaign.question}</StyledQuestion>
          <StyledForm>
            {range(11).map(index => (
              <StyledRadioButton htmlFor={`score-${index}`} checked={checkedIndex === index} key={`score-${index}`}>
                <input
                  type="radio"
                  name="rating"
                  id={`score-${index}`}
                  value={index}
                  onChange={e => e.target.checked && setCheckedIndex(index)}
                />
                {index}
              </StyledRadioButton>
            ))}
          </StyledForm>
          <StyledRadioGuideMessage>
            <div>{state?.npsCampaign.scoreMinGuideMessage}</div>
            <div>{state?.npsCampaign.scoreMaxGuideMessage}</div>
          </StyledRadioGuideMessage>
          <StyledCommentTitle>{state?.npsCampaign.commentTitle}</StyledCommentTitle>
          <StyledCommentTextArea
            placeholder={state?.npsCampaign.commentPlaceholder}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
          />
          {state?.npsCampaign.requestInterview && (
            <>
              <StyledInterviewTitle>{state?.npsCampaign.extraInfo.interview?.interviewTitle}</StyledInterviewTitle>
              <StyledRadioWrapper>
                <RadioButton
                  id="attendInterview"
                  showName
                  name="예"
                  value="true"
                  selectedValue={attendInterview}
                  onChange={() => setAttendInterview('true')}
                />
                <RadioButton
                  id="attendInterview"
                  showName
                  name="아니요"
                  value="false"
                  selectedValue={attendInterview}
                  onChange={() => setAttendInterview('false')}
                />
              </StyledRadioWrapper>
              <StyledInterviewDescription>
                {state?.npsCampaign.extraInfo.interview?.interviewDescription}
              </StyledInterviewDescription>
            </>
          )}
          <StyledButtons>
            <StyledButton label="디리토에 보내기" onClick={handleSubmit} disabled={!enableSendButton} />
            <StyledButton label="닫기" buttonType={ButtonType.SecondaryNoBorder} onClick={handleClose} />
          </StyledButtons>
        </StyledModal>
      ) : (
        <StyledModal>
          <StyledPreInterviewTitle>{state?.npsCampaign.extraInfo.interview?.preInterviewTitle}</StyledPreInterviewTitle>
          <StyledInterviewDescription>
            {state?.npsCampaign.extraInfo.interview?.preInterviewDescription}
          </StyledInterviewDescription>
          <StyledButtons>
            <StyledExternalLinkButton
              label="사전 설문하러 가기"
              href={
                `${
                  profile?.isAuthor
                    ? state?.npsCampaign.extraInfo.interview?.preInterviewAuthorLink
                    : state?.npsCampaign.extraInfo.interview?.preInterviewReaderLink
                }#profile_id=${profile?.id}` || ''
              }
              onClick={handleClose}
            />
            <StyledButton label="닫기" buttonType={ButtonType.SecondaryNoBorder} onClick={handleClose} />
          </StyledButtons>
        </StyledModal>
      )}
    </ModalLayout>
  );
};

const StyledRadioGuideMessage = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  ${Typography.XXSmall};
  color: ${Color.Gray600};
`;

const StyledForm = styled.form`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  border: 1px solid ${Color.Gray300};
  border-radius: 4px;
  overflow: hidden;
`;

const StyledRadioButton = styled.label(
  ({ checked }: { checked: boolean }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 48px;
    border-left: 1px solid ${Color.Gray300};
    color: ${checked ? Color.White : Color.Gray400};
    background-color: ${checked ? Color.Primary500 : 'transparent'};
    ${Typography.XXSmall};

    &:first-child {
      border-left: none;
    }

    > input {
      position: absolute;
      opacity: 0;
    }
  `,
);

const StyledModal = styled.div`
  width: 100vw;
  border-radius: 16px 16px 0 0;
  padding: 32px 24px 0 24px;

  @media (${isPC}) {
    width: 400px;
    border-radius: 16px;
    padding: 32px 24px 16px 24px;
  }
  background-color: ${Color.White};

  display: flex;
  flex-direction: column;
  align-items: center;
  ${SafeAreaBottomStyles(16)}
`;

const StyledQuestion = styled.p`
  font-weight: ${FontWeight.Bold};
  ${Typography.Small};
  color: ${Color.Black};
  text-align: left;
  width: 100%;
  word-break: break-all;
  word-wrap: anywhere;
`;

const StyledPreInterviewTitle = styled.p`
  font-weight: ${FontWeight.Bold};
  ${Typography.Small};
  color: ${Color.Black};
  text-align: left;
  width: 100%;
  word-break: break-all;
  word-wrap: anywhere;
  margin-bottom: 12px;
`;

const StyledCommentTitle = styled.p`
  margin-top: 32px;
  font-weight: ${FontWeight.Bold};
  ${Typography.Small};
  color: ${Color.Black};
  text-align: left;
  width: 100%;
`;

const StyledButtons = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const StyledExternalLinkButton = styled(ExternalLinkButton)`
  width: 100%;
`;

const StyledCommentTextArea = styled(TextArea)`
  width: 100%;
  height: 90px;
  border: 1px solid ${Color.Gray400};
  border-radius: 4px;
  padding: 13px 16px;
  margin: 16px 0;
  ${Typography.XXSmall};

  &:focus {
    outline: none;
    border: 1px solid ${Color.Primary500};
  }
`;

const StyledInterviewTitle = styled.p`
  margin-top: 16px;
  font-weight: ${FontWeight.Bold};
  ${Typography.Small};
  color: ${Color.Black};
  text-align: left;
  width: 100%;
`;

const StyledRadioWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  margin: 22px 0;

  > * {
    flex: 1;
  }
`;

const StyledInterviewDescription = styled.div`
  ${Typography.XXSmall};
  color: ${Color.Gray600};
  font-weight: ${FontWeight.Medium};
  margin-bottom: 16px;
`;
